import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  getTeacherLessonSubmissionSummary,
  getTeacherLessonSubmissions,
  getTeacherSubmissionSnapshot,
  type TeacherLessonSubmissionAttempt,
  type TeacherLessonSubmissionSummary,
  type TeacherSubmissionSnapshot,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { ReadOnlyPythonCode } from "../student-ide/ReadOnlyPythonCode";

import ProgressDonut from "./charts/ProgressDonut";
import { ProgressStatusChip } from "../student/ProgressStatusChip";
import {
  ASSIGNMENT_PROGRESS_EMOJI,
  type AssignmentProgressStatus,
} from "../student/assignmentProgressStatus";

const STATE_FILTER_OPTIONS: { value: AssignmentProgressStatus; label: string }[] = [
  { value: "not_started", label: `${ASSIGNMENT_PROGRESS_EMOJI.not_started} Not started` },
  { value: "in_progress", label: `${ASSIGNMENT_PROGRESS_EMOJI.in_progress} In progress` },
  { value: "needs_help", label: `${ASSIGNMENT_PROGRESS_EMOJI.needs_help} Needs help` },
  { value: "complete", label: `${ASSIGNMENT_PROGRESS_EMOJI.complete} Complete` },
];

const ALL_STATES = "all";

/** Compact timestamp without seconds, intended to render on a single line. */
function formatWhenShort(iso: string | null | undefined): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function stateEmoji(state: AssignmentProgressStatus): string {
  return ASSIGNMENT_PROGRESS_EMOJI[state];
}

/** Display name = the part of the email before the @ sign. */
function emailName(email: string): string {
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

function statusLabel(row: { progressStatus: AssignmentProgressStatus }) {
  return <ProgressStatusChip status={row.progressStatus} />;
}

function attemptLabel(a: TeacherLessonSubmissionAttempt): string {
  const when = formatWhenShort(a.createdAt);
  if (a.requiredPassed) return `${when} · passed`;
  if (a.submissionKind === "gave_up") return `${when} · not passed`;
  return `${when} · not passed`;
}

type ReviewCheck = { key: string; name: string; passed: boolean; required: boolean; message?: string };

function testResultsList(results: unknown): ReviewCheck[] {
  if (!Array.isArray(results)) return [];
  return results.map((item, i) => {
    const o = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
    const passed = o.passed === true;
    const rawMsg = typeof o.message === "string" ? o.message.trim() : "";
    const id = typeof o.lessonTestId === "string" && o.lessonTestId ? o.lessonTestId : `test-${i}`;
    const displayName = typeof o.displayName === "string" ? o.displayName.trim() : "";
    const required = o.isRequired !== false;
    return {
      key: id,
      name: displayName || `Check ${i + 1}`,
      passed,
      required,
      ...(rawMsg ? { message: rawMsg } : {}),
    };
  });
}

export default function TeacherLessonSubmissionsPage() {
  const { cohortId, lessonId } = useParams<{ cohortId: string; lessonId: string }>();
  const [searchParams] = useSearchParams();
  const lessonTitle = searchParams.get("title") ?? "Lesson";
  const studentFromUrl = searchParams.get("student")?.trim() ?? "";
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<TeacherLessonSubmissionSummary | null>(null);
  const [attempts, setAttempts] = useState<TeacherLessonSubmissionAttempt[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [selectedAttemptId, setSelectedAttemptId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<TeacherSubmissionSnapshot | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [stateFilter, setStateFilter] = useState<AssignmentProgressStatus | typeof ALL_STATES>(ALL_STATES);

  const reload = useCallback(async () => {
    if (!bearer || !cohortId || !lessonId) return;
    setLoading(true);
    setError(null);
    try {
      const [sum, att] = await Promise.all([
        getTeacherLessonSubmissionSummary(bearer, cohortId, lessonId),
        getTeacherLessonSubmissions(bearer, cohortId, lessonId),
      ]);
      setSummary(sum);
      setAttempts(att.attempts);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load submissions");
      setSummary(null);
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  }, [bearer, cohortId, lessonId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!studentFromUrl || !summary) return;
    const match = summary.students.find((s) => s.studentUserId === studentFromUrl);
    if (match) setSelectedStudentId(match.studentUserId);
  }, [studentFromUrl, summary]);

  const attemptsForStudent = useMemo(() => {
    if (!selectedStudentId) return [];
    return attempts.filter((a) => a.studentUserId === selectedStudentId);
  }, [attempts, selectedStudentId]);

  useEffect(() => {
    if (!selectedStudentId) {
      setSelectedAttemptId(null);
      setSnapshot(null);
      return;
    }
    const first = attemptsForStudent[0];
    setSelectedAttemptId(first?.id ?? null);
  }, [selectedStudentId, attemptsForStudent]);

  useEffect(() => {
    if (!bearer || !cohortId || !lessonId || !selectedAttemptId) {
      setSnapshot(null);
      return;
    }
    let cancelled = false;
    setSnapshotLoading(true);
    void (async () => {
      try {
        const data = await getTeacherSubmissionSnapshot(bearer, cohortId, lessonId, selectedAttemptId);
        if (!cancelled) setSnapshot(data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Could not load submission snapshot");
          setSnapshot(null);
        }
      } finally {
        if (!cancelled) setSnapshotLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, cohortId, lessonId, selectedAttemptId]);

  const selectedStudent = summary?.students.find((s) => s.studentUserId === selectedStudentId) ?? null;

  const visibleStudents = useMemo(() => {
    const students = (summary?.students ?? []).filter(
      (s) => stateFilter === ALL_STATES || s.progressStatus === stateFilter
    );
    return [...students].sort((a, b) =>
      emailName(a.email).localeCompare(emailName(b.email), undefined, { sensitivity: "base" })
    );
  }, [summary, stateFilter]);

  const reviewChecks = useMemo(() => testResultsList(snapshot?.testResults), [snapshot]);

  const donutCounts = useMemo(() => {
    const c = summary?.counts;
    const complete = c?.completed ?? 0;
    const inProgress = c?.inProgress ?? 0;
    const needsHelp = c?.needsHelp ?? 0;
    const notStarted = c?.notStarted ?? 0;
    const total = complete + inProgress + needsHelp + notStarted;
    return {
      complete,
      inProgress,
      needsHelp,
      notStarted,
      passRate: total > 0 ? Math.round((complete / total) * 100) : null,
    };
  }, [summary]);

  return (
    <div className="teacher-page">
      <header className="teacher-page-header">
        <p className="eyebrow">
          <Link to={`/teacher/cohorts/${cohortId ?? ""}`}>← Back to cohort</Link>
        </p>
        <h1 className="teacher-page-title">{lessonTitle}</h1>
        <p className="teacher-page-lede muted">
          Every time a student clicks Check My Work, their code and check results are saved here, including failed
          attempts, so you can see what they tried and help them improve.
        </p>
      </header>

      {error ? (
        <div className="warn-banner teacher-pad" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="muted teacher-pad">Loading submissions…</p>
      ) : summary ? (
        <>
          <section className="teacher-panel teacher-pad">
            <h2 className="teacher-panel-title">Roster summary</h2>
            <div className="submissions-roster-summary">
              <ProgressDonut
                complete={donutCounts.complete}
                inProgress={donutCounts.inProgress}
                needsHelp={donutCounts.needsHelp}
                notStarted={donutCounts.notStarted}
                passRate={donutCounts.passRate}
                hideLegend
              />
              <p className="muted small submissions-roster-legend">
                Active roster: <strong>{summary.rosterActive}</strong>
                {" · "}
                {ASSIGNMENT_PROGRESS_EMOJI.complete} Complete: <strong>{summary.counts.completed}</strong>
                {" · "}
                {ASSIGNMENT_PROGRESS_EMOJI.in_progress} In progress: <strong>{summary.counts.inProgress}</strong>
                {" · "}
                {ASSIGNMENT_PROGRESS_EMOJI.needs_help} Needs help: <strong>{summary.counts.needsHelp}</strong>
                {" · "}
                {ASSIGNMENT_PROGRESS_EMOJI.not_started} Not started: <strong>{summary.counts.notStarted}</strong>
              </p>
            </div>
          </section>

          <div className="teacher-split submissions-split">
            <section className="teacher-panel">
              <div className="submissions-students-head">
                <h2 className="teacher-panel-title">Students</h2>
                <label className="field submissions-state-filter">
                  <span className="field-label">Filter by state</span>
                  <select
                    className="input"
                    value={stateFilter}
                    onChange={(ev) => setStateFilter(ev.target.value as AssignmentProgressStatus | typeof ALL_STATES)}
                  >
                    <option value={ALL_STATES}>All states ({summary.students.length})</option>
                    {STATE_FILTER_OPTIONS.map((opt) => {
                      const count = summary.students.filter((s) => s.progressStatus === opt.value).length;
                      return (
                        <option key={opt.value} value={opt.value}>
                          {opt.label} ({count})
                        </option>
                      );
                    })}
                  </select>
                </label>
              </div>
              <p className="muted small submissions-students-hint">Select a student to review their attempts.</p>
              <div className="teacher-table-wrap">
                <table className="teacher-table submissions-students-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Status</th>
                      <th aria-label="Open" />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleStudents.map((s) => (
                      <tr
                        key={s.studentUserId}
                        className={`submissions-student-row${
                          selectedStudentId === s.studentUserId ? " teacher-row-selected" : ""
                        }`}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedStudentId === s.studentUserId}
                        onClick={() => setSelectedStudentId(s.studentUserId)}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            setSelectedStudentId(s.studentUserId);
                          }
                        }}
                      >
                        <td>
                          <strong className="submissions-student-name" title={s.email}>
                            {emailName(s.email)}
                          </strong>
                          {s.lastAttemptAt ? (
                            <div className="muted small submissions-student-when">
                              Last: {formatWhenShort(s.lastAttemptAt)}
                            </div>
                          ) : null}
                        </td>
                        <td>{statusLabel(s)}</td>
                        <td className="submissions-student-chevron" aria-hidden>
                          ›
                        </td>
                      </tr>
                    ))}
                    {visibleStudents.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="muted small">
                          No students match this state.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="teacher-panel submission-review">
              <h2 className="teacher-panel-title">Submission review</h2>
              {!selectedStudent ? (
                <p className="muted submission-review-empty">
                  Select a student to review their Check My Work attempts.
                </p>
              ) : attemptsForStudent.length === 0 ? (
                <p className="muted submission-review-empty">
                  {selectedStudent.email} hasn&apos;t run Check My Work yet.
                </p>
              ) : (
                <div className="submission-review-body">
                  <div className="submission-review-head">
                    <div className="submission-review-who">
                      <span className="submission-review-emoji" aria-hidden>
                        {stateEmoji(selectedStudent.progressStatus)}
                      </span>
                      <div>
                        <p className="submission-review-email">{selectedStudent.email}</p>
                        <p className="muted small">
                          {attemptsForStudent.length} attempt
                          {attemptsForStudent.length === 1 ? "" : "s"} · newest first
                        </p>
                      </div>
                    </div>
                    {attemptsForStudent.length > 1 ? (
                      <label className="field submission-review-attempt">
                        <span className="field-label">Attempt</span>
                        <select
                          className="input"
                          value={selectedAttemptId ?? ""}
                          onChange={(ev) => setSelectedAttemptId(ev.target.value || null)}
                        >
                          {attemptsForStudent.map((a, i) => (
                            <option key={a.id} value={a.id}>
                              #{attemptsForStudent.length - i} · {attemptLabel(a)}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                  </div>

                  {snapshotLoading ? (
                    <p className="muted">Loading code snapshot…</p>
                  ) : snapshot ? (
                    <>
                      <div
                        className={`submission-result-banner submission-result-banner--${
                          snapshot.requiredPassed ? "pass" : "fail"
                        }`}
                      >
                        <span className="submission-result-status">
                          {snapshot.requiredPassed
                            ? "✓ Required checks passed"
                            : "✗ Required checks not passed"}
                        </span>
                        {reviewChecks.length > 0 ? (
                          <span className="submission-result-count">
                            {reviewChecks.filter((c) => c.passed).length}/{reviewChecks.length} checks passed
                          </span>
                        ) : null}
                        <span className="muted small submission-result-when">
                          {formatWhenShort(snapshot.createdAt)}
                        </span>
                      </div>

                      <div className="submission-section">
                        <h3 className="submission-subhead">Check results</h3>
                        {reviewChecks.length === 0 ? (
                          <p className="muted small">No check results recorded for this attempt.</p>
                        ) : (
                          <ul className="submission-check-list">
                            {reviewChecks.map((c) => (
                              <li
                                key={c.key}
                                className={`submission-check submission-check--${c.passed ? "pass" : "fail"}`}
                              >
                                <span className="submission-check-icon" aria-hidden>
                                  {c.passed ? "✓" : "✗"}
                                </span>
                                <span className="submission-check-main">
                                  <span className="submission-check-name">
                                    {c.name}
                                    {!c.required ? (
                                      <span className="submission-check-tag">optional</span>
                                    ) : null}
                                  </span>
                                  {c.message ? (
                                    <span className="submission-check-msg">{c.message}</span>
                                  ) : null}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="submission-section submission-actions">
                        <Link
                          to={`/teacher/cohorts/${encodeURIComponent(cohortId ?? "")}/lessons/${encodeURIComponent(lessonId ?? "")}/submissions/${encodeURIComponent(snapshot.attemptId)}/run?${new URLSearchParams({
                            title: lessonTitle,
                            student: snapshot.studentEmail,
                          }).toString()}`}
                          className="btn primary"
                        >
                          Run as student
                        </Link>
                        <span className="muted small">
                          Opens the full IDE with this attempt&apos;s code: run, debug, and re-check their work.
                        </span>
                      </div>

                      <div className="submission-section">
                        <h3 className="submission-subhead">Submitted code (main.py)</h3>
                        <ReadOnlyPythonCode
                          code={
                            snapshot.snapshot?.mainPy?.trim()
                              ? snapshot.snapshot.mainPy
                              : "# (empty main.py)"
                          }
                          ariaLabel={`Submitted main.py for ${snapshot.studentEmail}`}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="muted">No snapshot available for this attempt.</p>
                  )}
                </div>
              )}
            </section>
          </div>
        </>
      ) : null}
    </div>
  );
}
